import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  WhatsApp,
  QrCode2,
  CheckCircle,
  Error,
  Refresh,
  Close,
  LinkOff,
} from "@mui/icons-material";
import {
  createWhatsAppInstance,
  getWhatsAppQRCode,
  getWhatsAppStatus,
  logoutWhatsAppInstance,
} from "../services/whatsappApi";

// Nome da instância do WhatsApp
const WHATSAPP_INSTANCE_NAME = "whatsapp-flowpet-madre";

interface WhatsAppConfigProps {
  onStatusChange?: (connected: boolean) => void;
  compact?: boolean;
}

const WhatsAppConfig: React.FC<WhatsAppConfigProps> = ({
  onStatusChange,
  compact = false,
}) => {
  const [status, setStatus] = useState<
    "checking" | "connected" | "disconnected" | "error"
  >("checking");
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  // Estado para o menu de ações no modo compacto
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Verificar status da conexão
  const checkStatus = useCallback(async () => {
    try {
      const result = await getWhatsAppStatus(WHATSAPP_INSTANCE_NAME);
      const newStatus = result.connected ? "connected" : "disconnected";
      setStatus(newStatus);
      onStatusChange?.(result.connected);
      return result.connected;
    } catch (err) {
      setStatus("error");
      onStatusChange?.(false);
      return false;
    }
  }, [onStatusChange]);

  // Efeito para fechar o modal automaticamente quando conectar
  useEffect(() => {
    if (status === "connected" && dialogOpen) {
      setDialogOpen(false);
      setQrCode(null);
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [status, dialogOpen, pollingInterval]);

  // Verificar status inicial
  useEffect(() => {
    checkStatus();
  }, []);

  // Limpar interval ao desmontar
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Criar instância e obter QR Code
  const handleConnect = async () => {
    setLoading(true);
    setError(null);
    setQrCode(null);

    try {
      // Primeiro, tentar criar a instância (se já existir, não tem problema)
      await createWhatsAppInstance(WHATSAPP_INSTANCE_NAME);

      // Obter QR Code
      const qrResult = await getWhatsAppQRCode(WHATSAPP_INSTANCE_NAME);

      if (qrResult.success && qrResult.qrcode) {
        setQrCode(qrResult.qrcode);
        setDialogOpen(true);

        // Iniciar polling para verificar quando conectar
        const interval = setInterval(async () => {
          const connected = await checkStatus();
          // O useEffect cuidará de fechar o modal quando connected for true
          if (connected) {
            clearInterval(interval);
            setPollingInterval(null);
          }
        }, 3000); // Verificar a cada 3 segundos

        setPollingInterval(interval);
      } else if (qrResult.success && !qrResult.qrcode) {
        // Já está conectado
        setStatus("connected");
        onStatusChange?.(true);
      } else {
        setError(qrResult.error || "Não foi possível obter o QR Code");
      }
    } catch (err) {
      const errorMessage = (err as any).message || "Erro ao conectar WhatsApp";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Atualizar QR Code
  const handleRefreshQR = async () => {
    setLoading(true);
    try {
      const qrResult = await getWhatsAppQRCode(WHATSAPP_INSTANCE_NAME);
      if (qrResult.qrcode) {
        setQrCode(qrResult.qrcode);
      }
    } catch (err) {
      setError("Erro ao atualizar QR Code");
    } finally {
      setLoading(false);
    }
  };

  // Desconectar
  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await logoutWhatsAppInstance(WHATSAPP_INSTANCE_NAME);
      setStatus("disconnected");
      onStatusChange?.(false);
      handleCloseMenu();
    } catch (err) {
      setError("Erro ao desconectar");
    } finally {
      setLoading(false);
    }
  };

  // Fechar modal
  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Handlers do menu
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCheckStatus = async () => {
    await checkStatus();
    handleCloseMenu();
  };

  // Renderização compacta (para header ou sidebar)
  if (compact) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip
          title={
            status === "connected"
              ? "Clique para opções"
              : "Clique para conectar"
          }
        >
          <Chip
            icon={<WhatsApp />}
            label={status === "connected" ? "Conectado" : "Desconectado"}
            color={status === "connected" ? "success" : "default"}
            size="small"
            onClick={status === "connected" ? handleOpenMenu : handleConnect}
            sx={{ cursor: "pointer" }}
          />
        </Tooltip>

        {/* Menu de opções para conectado */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <MenuItem onClick={handleCheckStatus} disabled={loading}>
            <ListItemIcon>
              <Refresh fontSize="small" />
            </ListItemIcon>
            <ListItemText>Verificar Status</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDisconnect} disabled={loading}>
            <ListItemIcon>
              <LinkOff fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ color: "error" }}>
              Desconectar
            </ListItemText>
          </MenuItem>
        </Menu>

        {/* Dialog de QR Code */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WhatsApp color="success" />
              Conectar WhatsApp
            </Box>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {qrCode ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Escaneie o QR Code abaixo com seu WhatsApp
                </Typography>
                <Box
                  component="img"
                  src={qrCode}
                  alt="QR Code WhatsApp"
                  sx={{
                    maxWidth: "100%",
                    width: 300,
                    height: 300,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    my: 2,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Abra o WhatsApp → Menu (3 pontos) → Aparelhos conectados →
                  Conectar um aparelho
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleRefreshQR}
              startIcon={<Refresh />}
              disabled={loading}
            >
              Atualizar QR Code
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Renderização completa
  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <WhatsApp sx={{ fontSize: 40, color: "#25D366" }} />
        <Box>
          <Typography variant="h6">Configuração do WhatsApp</Typography>
          <Typography variant="body2" color="text.secondary">
            Conecte seu WhatsApp para enviar notificações
          </Typography>
        </Box>
      </Box>

      {/* Status */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography variant="body1">Status:</Typography>
        {status === "checking" && (
          <Chip icon={<CircularProgress size={16} />} label="Verificando..." />
        )}
        {status === "connected" && (
          <Chip icon={<CheckCircle />} label="Conectado" color="success" />
        )}
        {status === "disconnected" && (
          <Chip icon={<Error />} label="Desconectado" color="warning" />
        )}
        {status === "error" && (
          <Chip icon={<Error />} label="Erro" color="error" />
        )}
      </Box>

      {/* Erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Ações */}
      <Box sx={{ display: "flex", gap: 2 }}>
        {status !== "connected" ? (
          <Button
            variant="contained"
            color="success"
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <QrCode2 />
              )
            }
            onClick={handleConnect}
            disabled={loading}
          >
            {loading ? "Conectando..." : "Conectar via QR Code"}
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={checkStatus}
              disabled={loading}
            >
              Verificar Status
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOff />}
              onClick={handleDisconnect}
              disabled={loading}
            >
              Desconectar
            </Button>
          </>
        )}
      </Box>

      {/* Dialog de QR Code */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WhatsApp color="success" />
            Conectar WhatsApp
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {qrCode ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Escaneie o QR Code abaixo com seu WhatsApp para conectar
              </Alert>
              <Box
                component="img"
                src={qrCode}
                alt="QR Code WhatsApp"
                sx={{
                  maxWidth: "100%",
                  width: 300,
                  height: 300,
                  border: "2px solid",
                  borderColor: "success.main",
                  borderRadius: 2,
                  my: 2,
                  boxShadow: 3,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>Como escanear:</strong>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                1. Abra o WhatsApp no seu celular
                <br />
                2. Toque em Menu (⋮) ou Configurações
                <br />
                3. Toque em &quot;Aparelhos conectados&quot;
                <br />
                4. Toque em &quot;Conectar um aparelho&quot;
                <br />
                5. Aponte a câmera para este QR Code
              </Typography>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  O QR Code expira em alguns segundos. Se não funcionar, clique
                  em &quot;Atualizar QR Code&quot;.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={60} color="success" />
              <Typography sx={{ mt: 2 }}>Gerando QR Code...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleRefreshQR}
            startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            disabled={loading}
          >
            Atualizar QR Code
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WhatsAppConfig;
